import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Generates a single-page mailing invoice PDF designed for a standard
 * #10 double-window envelope (trifold). The business return address and
 * patient address are positioned to align with the two envelope windows.
 *
 * Fold guide (letter = 8.5" x 11" = 215.9mm x 279.4mm):
 *   Top fold:    0    – 93.1mm
 *   Middle fold: 93.1 – 186.3mm  ← this face shows through the envelope
 *   Bottom fold: 186.3 – 279.4mm (tucked behind)
 *
 * Window 1 (return/business): calibrated y=116mm, x=15mm
 * Window 2 (recipient):       calibrated y=160mm, x=15mm
 */
export const generateMailingInvoice = async (formData) => {
  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();   // 215.9mm
  const leftMargin = 15;
  const rightMargin = 15;
  const contentWidth = pageW - leftMargin - rightMargin;

  // ── Logo ──────────────────────────────────────────────────────────────────
  const logoWidth = 22;
  const logoHeight = 22;
  const logoX = pageW - rightMargin - logoWidth;
  const logoY = 4;

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = src;
    });

  try {
    const imgData = await loadImage('../../img/health_guardian.png');
    doc.addImage(imgData, 'PNG', logoX, logoY, logoWidth, logoHeight, undefined, 'FAST');
  } catch {
    doc.setFont('helvetica', 'italic').setFontSize(9).setTextColor('#999999');
    doc.text('Health Guardian Imaging', logoX, logoY + 10);
  }

  // ── Header ────────────────────────────────────────────────────────────────
  let y = 12;
  doc.setFont('helvetica', 'bold').setFontSize(22).setTextColor('#000000');
  doc.text('INVOICE', leftMargin, y);

  // ── Divider ───────────────────────────────────────────────────────────────
  y += 7;
  doc.setDrawColor('#cccccc').setLineWidth(0.3).line(leftMargin, y, pageW - rightMargin, y);
  y += 6;

  // ── Charges table ─────────────────────────────────────────────────────────
  if (y < logoY + logoHeight + 4) y = logoY + logoHeight + 4;

  const invoiceDos = formData.dateOfService || 'N/A';

  const chargeRows = (formData.charges || [])
    .filter((c) => c.qty >= 1)
    .map((c) => [
      c.description,
      c.date || invoiceDos,
      String(c.qty),
      `$${Number(c.unitPrice).toFixed(2)}`,
      `$${(c.qty * c.unitPrice).toFixed(2)}`,
    ]);

  autoTable(doc, {
    head: [['Description', 'Date of Service', 'QTY', 'Unit Price', 'Amount']],
    body: chargeRows.length > 0 ? chargeRows : [['No charges recorded', '', '', '', '']],
    startY: y,
    margin: { left: leftMargin, right: rightMargin },
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 30 },
      2: { halign: 'right', cellWidth: 16 },
      3: { halign: 'right', cellWidth: 26 },
      4: { halign: 'right', cellWidth: 26 },
    },
  });

  y = doc.lastAutoTable.finalY + 5;

  // ── Totals block ──────────────────────────────────────────────────────────
  const amountDue = Number(formData.amountDue) || 0;

  const totalCardPaidInv = (formData.payments || []).reduce(
    (sum, p) => sum + (Number(p.amountPaid) || 0), 0
  );
  const totalCashPaidInv = (formData.cashPayments || []).reduce(
    (sum, cp) => sum + (Number(cp.amount_paid) - Number(cp.change_due) || 0), 0
  );
  const totalCheckPaidInv = (formData.checkPayments || []).reduce(
    (sum, chk) => sum + (Number(chk.amount_paid) || 0), 0
  );
  const totalPaid = totalCardPaidInv + totalCashPaidInv + totalCheckPaidInv;
  const balance = Math.max(amountDue - totalPaid, 0);

  const totalsX = pageW - rightMargin - 70;
  const valueX = pageW - rightMargin;

  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor('#555555');
  doc.text('Total Charges:', totalsX, y);
  doc.text(`$${amountDue.toFixed(2)}`, valueX, y, { align: 'right' });
  y += 6;

  doc.text('Total Paid:', totalsX, y);
  doc.text(`$${totalPaid.toFixed(2)}`, valueX, y, { align: 'right' });
  y += 3;

  doc.setDrawColor('#cccccc').setLineWidth(0.3).line(totalsX, y, valueX, y);
  y += 5;
  doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor('#000000');
  doc.text('AMOUNT DUE:', totalsX, y);
  doc.text(`$${balance.toFixed(2)}`, valueX, y, { align: 'right' });

  // ── Fold line guides ───────────────────────────────────────────────────────
  const fold1Y = 93.1;
  const fold2Y = 186.3;
  doc.setDrawColor('#dddddd').setLineWidth(0.2);
  doc.setLineDashPattern([2, 2], 0);
  doc.line(leftMargin, fold1Y, pageW - rightMargin, fold1Y);
  doc.line(leftMargin, fold2Y, pageW - rightMargin, fold2Y);
  doc.setLineDashPattern([], 0);

  doc.setFont('helvetica', 'normal').setFontSize(6).setTextColor('#bbbbbb');
  doc.text('— fold —', pageW / 2, fold1Y - 1, { align: 'center' });
  doc.text('— fold —', pageW / 2, fold2Y - 1, { align: 'center' });

  // ── Window 1: Business / Return Address ───────────────────────────────────
  const win1Y = 116;
  doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor('#000000');
  doc.text('Health Guardian Imaging', leftMargin, win1Y);
  doc.setFont('helvetica', 'normal').setFontSize(9);
  doc.text('1505 E Bradford Pkwy, Suite B', leftMargin, win1Y + 5);
  doc.text('Springfield, MO 65804', leftMargin, win1Y + 10);
  doc.text('(417) 708-9043', leftMargin, win1Y + 15);

  // ── Window 2: Patient / Recipient Address ─────────────────────────────────
  const win2Y = 160;
  const { address = '', aptNumber = '', city = '', state = '', zip = '' } =
    formData.patientAddress || {};
  const addressLine2 = [city, state, zip].filter(Boolean).join(', ');

  doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor('#000000');
  doc.text(formData.responsibleName || 'Patient', leftMargin, win2Y);
  doc.setFont('helvetica', 'normal').setFontSize(9);
  if (address) {
    const streetLine = aptNumber ? `${address}, Apt ${aptNumber}` : address;
    doc.text(streetLine, leftMargin, win2Y + 5);
  }
  if (addressLine2) {
    doc.text(addressLine2, leftMargin, win2Y + (address ? 10 : 5));
  }

  // ── Payment instructions ───────────────────────────────────────────────────
  const instrY = 195;
  doc.setFont('helvetica', 'italic').setFontSize(8).setTextColor('#666666');
  doc.text(
    'Please remit payment to Health Guardian Imaging — 1505 E Bradford Pkwy, Suite B, Springfield, MO 65804 — or call (417) 708-9043.',
    leftMargin,
    instrY,
    { maxWidth: contentWidth }
  );

  // ── Download ──────────────────────────────────────────────────────────────
  const safeName = (formData.responsibleName || 'Patient').replace(/\s+/g, '_');
  doc.save(`HGI_Invoice_${safeName}_${formData.dateOfService || 'undated'}.pdf`);
};

  export const generateBill = async (formData, chargeData) => {
    const doc = new jsPDF();
    const leftMargin = 20;
    const contentWidth = 170;
    const baseLineHeight = 8;
    const elementSpacing = 4;
    const maxYPosition = 270;
    const logoWidth = 40;
    const logoHeight = 40;
    const xPositionLogo = doc.internal.pageSize.getWidth() - leftMargin - logoWidth;
    const yPositionLogo = 10;

    const logoImagePath = "../../img/health_guardian.png";

    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = (error) => reject(error);
        img.src = src;
      });
    };

    try {
      const imgData = await loadImage(logoImagePath);
      doc.addImage(imgData, "PNG", xPositionLogo, yPositionLogo, logoWidth, logoHeight, undefined, "FAST");
    } catch {
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.text("Health Guardian Logo", xPositionLogo, yPositionLogo + 10);
    }

    let y = 30;

    const checkPageOverflow = (linesCount = 1) => {
      const totalHeight = linesCount * baseLineHeight + elementSpacing;
      if (y + totalHeight > maxYPosition) {
        doc.addPage();
        y = 20;
      }
    };

  const addWrappedText = (text) => {
    const lines = doc.splitTextToSize(text || "N/A", contentWidth);
    checkPageOverflow(lines.length);
    doc.text(lines, leftMargin, y);
    y += lines.length * baseLineHeight;
  };

    const addStyledField = (label, value) => {
      checkPageOverflow();
      doc.setTextColor("#808080");
      doc.text(`${label}:`, leftMargin, y);
      const labelWidth = doc.getTextWidth(`${label}: `);
      doc.setTextColor("#000000");
      doc.text(`${value || "N/A"}`, leftMargin + labelWidth + 1, y);
      y += baseLineHeight + elementSpacing;
    };

    const drawCompanyInfo = () => {
        const company = { name: "1505 E Bradford Pkwy, Suite B", address: "Springfield, MO 65804", phone: "(417) 708-9043", email: `info@healthguardianimaging.com` }
        const pageWidth = doc.internal.pageSize.getWidth();
        const rightMargin = leftMargin; // same margin you use on the left
        const startX = pageWidth - rightMargin; // anchor to right side
        const startY = yPositionLogo + logoHeight + 10; // directly under the logo
        let lineY = startY;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10); // smaller than header

        if (company.name) {
            doc.text(company.name, startX, lineY, { align: "right" });
            lineY += elementSpacing;
        }
        if (company.address) {
            doc.text(company.address, startX, lineY, { align: "right" });
            lineY += elementSpacing;
        }
        if (company.phone) {
            doc.text(company.phone, startX, lineY, { align: "right" });
            lineY += elementSpacing;
        }
        if (company.email) {
            doc.text(company.email, startX, lineY, { align: "right" });
            lineY += elementSpacing;
        }

        // update y so subsequent content doesn’t overlap
        // y = lineY + elementSpacing;
        };


    const drawHeader = (text) => {
      checkPageOverflow();
      doc.setTextColor("#000000");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      doc.text(text, leftMargin, y + baseLineHeight);
      y += baseLineHeight + elementSpacing;
      doc.setTextColor("#000000");
      y += elementSpacing;
    };

    drawHeader(`HGI Itemized Bill`);

    drawCompanyInfo();

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");


    // Responsible Party Info
    // addStyledField("Billed To:", formData.unableToConsent ? formData.reason || "N/A" : "N/A");
    // addStyledField("Relationship to patient:", formData.relationship);
    addStyledField("Patient Name ", chargeData.responsibleName);
    addStyledField("Date of Service ", chargeData.dateOfService);

    // Itemized Table
    const head = [["Description", "CPT Code", "Referring Provider", "Ordering Provider", "QTY", "Unit Price", "Amount"]];
    const body = chargeData.charges.filter(charge => charge.qty >= 1).map((c) => [
      c.description,
      c.cptCode || "—",
      c.referringProvider || "—",
      c.orderingProvider || "—",
      String(c.qty),
      `$${c.unitPrice.toFixed(2)}`,
      `$${(c.qty * c.unitPrice).toFixed(2)}`,
    ]);

    autoTable(doc, {
      head,
      body,
      startY: y + 10,
      styles: { font: "helvetica", fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [245, 245, 245], textColor: 20, fontStyle: "bold" },
      columnStyles: {
        1: { cellWidth: 20 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { halign: "right", cellWidth: 14 },
        5: { halign: "right", cellWidth: 22 },
        6: { halign: "right", cellWidth: 22 },
      },
    });

    y = doc.lastAutoTable.finalY + 20;

    const subtotal = chargeData.charges.reduce((sum, c) => sum + c.qty * c.unitPrice, 0);
    addStyledField("Total Charges", `$${subtotal.toFixed(2)}`);

    doc.save(`HealthGuardian_Itemized_Bill.pdf`);
  };

  
export const generateReceipt = async (formData) => {
  // logger.log(formData)

  const doc = new jsPDF();
  const leftMargin = 20;
  const contentWidth = 170;
  const baseLineHeight = 8;
  const elementSpacing = 4;
  const tinySpacing = 2;
  const maxYPosition = 300;
  const logoWidth = 40;
  const logoHeight = 40;
  const xPositionLogo = doc.internal.pageSize.getWidth() - leftMargin - logoWidth;
  const yPositionLogo = 10;

  const logoImagePath = "../../img/health_guardian.png";

  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (error) => reject(error);
      img.src = src;
    });
  };

  try {
    const imgData = await loadImage(logoImagePath);
    doc.addImage(imgData, "PNG", xPositionLogo, yPositionLogo, logoWidth, logoHeight, undefined, "FAST");
  } catch {
    doc.setFontSize(10);
    doc.setFont("helvetica", "italic");
    doc.text("Health Guardian Logo", xPositionLogo, yPositionLogo + 10);
  }

  let y = 10;

  const checkPageOverflow = (linesCount = 1) => {
    const totalHeight = linesCount * baseLineHeight + elementSpacing;
    if (y + totalHeight > maxYPosition) {
      doc.addPage();
      y = 20;
    }
  };

  const addStyledField = (label, value) => {
    checkPageOverflow();
    doc.setTextColor("#808080");
    doc.text(`${label}:`, leftMargin, y);
    const labelWidth = doc.getTextWidth(`${label}: `);
    doc.setTextColor("#000000");
    doc.text(`${value || "N/A"}`, leftMargin + labelWidth + 1, y);
    y += baseLineHeight;
  };

  const addWrappedText = (text) => {
    const lines = doc.splitTextToSize(text || "N/A", contentWidth);
    checkPageOverflow(lines.length);
    doc.text(lines, leftMargin, y);
    y += lines.length * baseLineHeight;
  };

  const drawCompanyInfo = () => {
    const company = formData.company || {
      name: "1505 E Bradford Pkwy, Suite B",
      address: "Springfield, MO 65804",
      phone: "(417) 708-9043",
      email: "info@healthguardianimaging.com"
    };
    const pageWidth = doc.internal.pageSize.getWidth();
    const rightMargin = leftMargin;
    const startX = pageWidth - rightMargin;
    const startY = yPositionLogo + logoHeight + 10;
    let lineY = startY;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    if (company.name) {
      doc.text(company.name, startX, lineY, { align: "right" });
      lineY += elementSpacing;
    }
    if (company.address) {
      doc.text(company.address, startX, lineY, { align: "right" });
      lineY += elementSpacing;
    }
    if (company.phone) {
      doc.text(company.phone, startX, lineY, { align: "right" });
      lineY += elementSpacing;
    }
    if (company.email) {
      doc.text(company.email, startX, lineY, { align: "right" });
      lineY += elementSpacing;
    }
  };

  const drawHeader = (text) => {
    checkPageOverflow();
    doc.setTextColor("#000000");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.text(text, leftMargin, y + baseLineHeight);
    y += baseLineHeight + elementSpacing;
    doc.setTextColor("#000000");
    y += elementSpacing;
  };

  const formatCardBrand = (brand) => {
    if (!brand) return "";
    return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
  };

  const formatPaymentMethod = (paymentData) => {
    if (!paymentData) return "N/A";
    const brand = formatCardBrand(paymentData.cardBrand);
    const last4 = paymentData.cardLast4;
    if (brand && last4) {
      return `${brand} •••• ${last4}`;
    }
    return "N/A";
  };

    // Header
    
    drawHeader(`HGI Itemized Receipt`);
    drawCompanyInfo();


    // Patient Info
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    addStyledField("Patient Name", formData.responsibleName);
    addStyledField("RIS Payment ID", formData?.payments[0]?.paymentId || formData?.cashPayments[0]?.paymentId || formData?.checkPayments[0]?.paymentId);
    addStyledField("Date of Service", formData.dateOfService);

    // The company info block is drawn under the logo (right side) at a fixed position and does
    // not advance `y`. Drop below it before the payment/charge tables so they don't overlap and
    // cut off the clinic address/phone/email.
    y = Math.max(y + elementSpacing, yPositionLogo + logoHeight + 30);



    // Card/Stripe Payments
    if (formData.payments && formData.payments.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text("Card/Stripe Payments", leftMargin, y);
      y += baseLineHeight;

      formData.payments.forEach((payment, index) => {
        doc.setFont("helvetica", "bold");
        doc.text(`Payment #${index + 1}`, leftMargin, y);
        y += baseLineHeight;

        doc.setFont("helvetica", "normal");
        addStyledField("Payment Intent ID", payment.paymentIntentId);
        addStyledField("Charge ID", payment.chargeId);
        // addStyledField("Receipt Number", payment.receiptNumber);
        // addWrappedText(`Receipt URL: ${payment.receiptURL}`);
        // addStyledField("Paid By", payment.payerName);
        // addStyledField("Payment Method", formatPaymentMethod(payment));
        addStyledField("Amount Paid", `$${Number(payment.amountPaid).toFixed(2)}`);
        // addStyledField("Balance Remaining", `$${Number(payment.balanceRemaining).toFixed(2)}`);

        y += elementSpacing;
      });
    }

    // Cash Payments Table
    if (formData.cashPayments && formData.cashPayments.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text("Cash Payments", leftMargin, y);
      // y += elementSpacing;

      autoTable(doc, {
        head: [["Amount Due", "Amount Paid", "Change Due", "Paid At"]], // , "Appointment ID" "Balance Remaining",
        body: formData.cashPayments.map(cp => [
          `$${Number(cp.amount_due).toFixed(2)}`,
          `$${Number(cp.amount_paid).toFixed(2)}`,
          `$${Number(cp.change_due).toFixed(2)}`,
          // `$${Number(cp.balance_remaining).toFixed(2)}`,
          cp.paid_at ? new Date(cp.paid_at).toLocaleString() : "N/A",
          // cp.appointments_id || "N/A"
        ]),
        startY: y + 5,
        styles: { font: "helvetica", fontSize: 10, cellPadding: 6 },
        headStyles: { fillColor: [245, 245, 245], textColor: 20, fontStyle: "bold" }
      });

      y = doc.lastAutoTable.finalY + 15;
    }

    // Check Payments Table
    if (formData.checkPayments && formData.checkPayments.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text("Check Payments", leftMargin, y);
      y += baseLineHeight;

      autoTable(doc, {
        head: [["Check #", "Payer Name", "Bank", "Amount Paid", "Paid At"]],
        body: formData.checkPayments.map(chk => [
          chk.check_number || "N/A",
          chk.payer_name || "N/A",
          chk.bank_name || "—",
          `$${Number(chk.amount_paid).toFixed(2)}`,
          chk.paid_at ? new Date(chk.paid_at).toLocaleString() : "N/A",
        ]),
        startY: y + 5,
        styles: { font: "helvetica", fontSize: 10, cellPadding: 6 },
        headStyles: { fillColor: [245, 245, 245], textColor: 20, fontStyle: "bold" }
      });

      y = doc.lastAutoTable.finalY + 15;
    }

    // Itemized Charges Table
    doc.setFont("helvetica", "bold");
      doc.text("Visit Charges", leftMargin, y);
      // y += elementSpacing;
    const head = [["Description", "CPT Code", "Referring Provider", "Ordering Provider", "QTY", "Unit Price", "Amount"]];
    const body = (formData.charges || [])
      .filter(charge => charge.qty >= 1)
      .map((c) => [
        c.description,
        c.cptCode || "—",
        c.referringProvider || "—",
        c.orderingProvider || "—",
        String(c.qty),
        `$${c.unitPrice.toFixed(2)}`,
        `$${(c.qty * c.unitPrice).toFixed(2)}`
      ]);

    autoTable(doc, {
      head,
      body,
      startY: y + 5,
      styles: { font: "helvetica", fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [245, 245, 245], textColor: 20, fontStyle: "bold" },
      columnStyles: {
        1: { cellWidth: 20 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { halign: "right", cellWidth: 14 },
        5: { halign: "right", cellWidth: 22 },
        6: { halign: "right", cellWidth: 22 },
      },
    });

    y = doc.lastAutoTable.finalY + 15;

    // Totals
    // Totals
    const subtotal = (formData.charges || []).reduce(
      (sum, c) => sum + (Number(c.qty) * Number(c.unitPrice) || 0), 
      0
    );

    const totalCardPaid = (formData.payments || []).reduce(
      (sum, p) => sum + (Number(p.amountPaid) || 0), 
      0
    );

    const totalCashPaid = (formData.cashPayments || []).reduce(
      (sum, cp) => sum + (Number(cp.amount_paid) - (Number(cp.change_due)) || 0),
      0
    );

    const totalCheckPaid = (formData.checkPayments || []).reduce(
      (sum, chk) => sum + (Number(chk.amount_paid) || 0),
      0
    );

    const grandTotalPaid = totalCardPaid + totalCashPaid + totalCheckPaid;
    const finalBalance = subtotal - grandTotalPaid;

    addStyledField("Total Charges", `$${subtotal.toFixed(2)}`);
    addStyledField("Total Card Paid", `$${totalCardPaid.toFixed(2)}`);
    addStyledField("Total Cash Paid", `$${totalCashPaid.toFixed(2)}`);
    addStyledField("Total Check Paid", `$${totalCheckPaid.toFixed(2)}`);
    addStyledField("Grand Total Paid", `$${grandTotalPaid.toFixed(2)}`);
    addStyledField("Final Balance Remaining", `$${finalBalance.toFixed(2)}`);

    doc.save(`HealthGuardian_Itemized_Receipt.pdf`);
  };